import { http } from '../../../configuration/api';
import {
	ApiResponse,
	CropDataResponse,
	LanguageDataResponse,
	Offer,
	OfferDetails,
	OfferDetailsResponse,
	Request,
	RequestDetailResponse,
	RequestType,
	SeasonalOneTimeOffer,
	UserDataResponse,
} from '../types';

// Re-export interface for type compatibility
export interface OffersListResponse {
	data: Offer[];
	pagination: {
		total_pages: number;
		current_page: number;
		total_items: number;
	};
}

export type OfferStatus = 'ongoing' | 'completed' | 'archived';

/**
 * Fetch requests based on status (ongoing, completed, or archive)
 */
export const getRequests = async (
	status: OfferStatus = 'ongoing',
	page: number = 1,
	limit: number = 10
): Promise<{ data: { requests: Request[] } }> => {
	try {
		// Map internal status to API status
		const apiStatus = status === 'ongoing' ? 'active' : status;

		const queryParams = new URLSearchParams({
			page: page.toString(),
			limit: limit.toString(),
			status: apiStatus,
		});

		const endpoint = `farmer-request-and-offer/one_time_and_seasonal?${queryParams.toString()}`;
		const response = await http.get<ApiResponse<any>>('requests', endpoint);

		// Handle the new API response structure
		const apiData = response.data.data;
		const requests = Array.isArray(apiData.data) ? apiData.data : [];

		return {
			data: {
				requests: requests.map((item: any) => ({
					...item,
					// Ensure compatibility with existing code
					request_id: item.request_id || item.id,
					active_for_days: item.days_remaining || 0,
					// Map new crops array to legacy crop field (use first crop if available)
					crop: item.crops && item.crops.length > 0 ? item.crops[0] : undefined,
					// Set status based on request_type or default to 'active'
					status: 'active', // API doesn't seem to return status, so default to active
				})),
			},
		};
	} catch (error: any) {
		console.error(`Error fetching ${status} requests:`, error);
		throw error;
	}
};

/**
 * Fetch offers for a specific request
 */
export const getOffersForRequest = async (
	requestId: string
): Promise<{ data: { offers: Offer[] } }> => {
	try {
		const response = await http.get<ApiResponse<any>>(
			'requests',
			`farmer-offers?request_id=${requestId}`
		);

		console.log('API Response (Seasonal):', response.data);
		
		const data = response.data.data;
		const offers = Array.isArray(data?.offers) ? data.offers : [];
		
		// Log the offers to see what we're getting from the API
		console.log('Original offers from API (Seasonal):', offers);
		
		// Each offer should already have its own end_date in the API response
		// We don't need to modify it, just ensure it's properly passed through
		
		return {
			data: {
				offers: offers,
			},
		};
	} catch (error: any) {
		console.error(`Error fetching offers for request ${requestId}:`, error);
		throw error;
	}
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use getRequests or getOffersForRequest instead
 */
export const getOffers = async (status: string = 'ongoing'): Promise<{ data: Offer[] }> => {
	try {
		const mappedStatus = status === 'active' ? 'ongoing' : (status as OfferStatus);
		const response = await getRequests(mappedStatus);

		return {
			data: [], // This function is now deprecated
		};
	} catch (error: any) {
		console.error('Error in legacy getOffers:', error);
		throw error;
	}
};

export async function acceptOffer(offerId: string) {
	let attempts = 0;
	const maxAttempts = 3;
	
	while (attempts < maxAttempts) {
		attempts++;
		try {
			console.log(`Accepting offer with ID: ${offerId} (attempt ${attempts}/${maxAttempts})`);
			
			const response = await http.post<ApiResponse<{ redirect_url?: string }>>(
				`offers`,
				`farmer/accept-offer/${offerId}`
			);
			
			console.log(`Accept offer response (attempt ${attempts}):`, response.data);
			
			// If the response contains a redirect_url, it's a paid offer
			// If not, it's a free offer that was accepted directly
			return response.data;
		} catch (error: any) {
			console.error(`Error accepting offer (attempt ${attempts}/${maxAttempts}):`, error);
			
			// If this is the last attempt, throw the error
			if (attempts >= maxAttempts) {
				throw error;
			}
			
			// Otherwise, wait a bit before retrying
			await new Promise(resolve => setTimeout(resolve, 1000));
		}
	}
	
	// This should never be reached due to the throw in the catch block
	throw new Error(`Failed to accept offer after ${maxAttempts} attempts`);
}

/**
 * Mark offer comment as read
 */
export const markCommentAsRead = async (offerId: string) => {
  try {
    const response = await http.put<ApiResponse<any>>(
      'requests',
      `/make-comment-as-read/${offerId}`
    );
    
    return response.data;
  } catch (error) {
    console.error('Error marking comment as read:', error);
    throw error;
  }
};

/**
 * Fetch request details by ID (adapted from specialist version for farmer use)
 */
export const getRequestDetailById = async (requestId: string): Promise<SeasonalOneTimeOffer> => {
	try {
		// 1. First, get the main request details
		const requestResponse = await http.get<RequestDetailResponse>(
			'requests',
			`farmer-request/by-id/${requestId}`
		);

		if (!requestResponse.data.success) {
			throw new Error(requestResponse.data.error || 'Failed to fetch request details');
		}

		const requestData = requestResponse.data.data;

		// 2. Create parallel requests for user, crop, and language data
		const [userResponse, cropsResponse, languagesResponse] = await Promise.all([
			http.get<UserDataResponse>('users', `request-user/${requestData.farmer_id}`),
			http.get<CropDataResponse>('crops', 'list/'),
			http.get<LanguageDataResponse>('languages', 'list/'),
		]);

		// 3. Process the data
		const userData = userResponse.data.data;

		// Find the crop data by crop_id
		const allCrops = cropsResponse.data.data;
		const cropData = allCrops.find((crop) => crop.id === requestData.crop_id) || {
			id: requestData.crop_id,
			name: 'Unknown Crop',
			image_url: '',
			country_code: 'en',
		};

		// Find the language data from language IDs
		const allLanguages = languagesResponse.data.data;
		const languageData = requestData.communication_language.map((langId) => {
			const language = allLanguages.find((lang) => lang.id === langId);
			return language || { id: langId, name: 'Unknown Language' };
		});

		// 4. Transform the data to match SeasonalOneTimeOffer structure
		const seasonalOnetimeOffer: SeasonalOneTimeOffer = {
			id: requestData.id,
			crop: {
				id: cropData.id,
				name: cropData.name,
				image_url: cropData.image_url,
			},
			farmer: {
				id: userData.id,
				user_id: userData.user_id,
				first_name: userData.first_name,
				last_name: userData.last_name,
				full_name: `${userData.first_name} ${userData.last_name}`,
				profile_image_url: userData.profile_image,
				country: {
					name: userData.country,
					alpha_two: userData.unicode?.alpha_two || '',
					alpha_three: userData.unicode?.alpha_three || '',
				},
			},
			sent_date: requestData.created_at,
			request_type:
				requestData.request_type === 'SEASONAL' ? RequestType.SEASONAL : RequestType.ONE_TIME,
			duration_days: '5', // Default or calculate from dates
			duration_timestamp: requestData.end_date,
			request_valid_for_days: '5', // Default or calculate from dates
			request_valid_for_timestamp: requestData.end_date,
			farm_details: {
				area: requestData.area,
				unit: requestData.unit,
				location: {
					address: requestData.location_name,
					latitude: requestData.latitude,
					longitude: requestData.longitude,
				},
			},
			farmer_id: requestData.farmer_id,
			specialist_id: requestData.specialist_id, // Add specialist_id from request data
			communication_languages: languageData,
			description: requestData.problem_description,
			price: 0, // Default price since there are no services
			currency: 'EUR', // Default currency
			start_date: requestData.start_date,
			end_date: requestData.end_date,
			attachments: requestData.request_files,
			// Calculate if date is still valid to determine if the offer is active
			is_paid: requestData.payment_status === 'paid',
			is_sent: requestData.offer_id !== null,
			offer_id: requestData.offer_id || undefined,
			request_id: requestData.request_id, // Add request_id for channelId lookup
			// Add payment_status and original_date
			payment_status: requestData.payment_status,
			original_date: requestData.original_date,
		};

		// 5. Get and add channel ID
		try {
			// Import the chatService directly to avoid circular dependencies
			const { chatService } = await import('services/chat/chatService');
			
			// Check if we have an offer ID available
			if (seasonalOnetimeOffer.offer_id) {
				// Use specialist_id from the request data if available
				if (seasonalOnetimeOffer.specialist_id) {
					const channelId = await chatService.getChannelIdByRequestAndUserId(
						requestData.request_id,
						seasonalOnetimeOffer.specialist_id
					);
					seasonalOnetimeOffer.channelId = channelId || '';
				} else {
					// Fallback to user_id if we can't get specialist ID
					const channelId = await chatService.getChannelIdByRequestAndUserId(
						requestData.request_id,
						userData.user_id
					);
					seasonalOnetimeOffer.channelId = channelId || '';
				}
			} else {
				// No offer ID, use user_id as fallback
				const channelId = await chatService.getChannelIdByRequestAndUserId(
					requestData.request_id,
					userData.user_id
				);
				seasonalOnetimeOffer.channelId = channelId || '';
			}
		} catch (error) {
			console.error(`Error fetching channelId for request ${requestData.request_id}:`, error);
			seasonalOnetimeOffer.channelId = '';
		}

		return seasonalOnetimeOffer;
	} catch (error: any) {
		console.error('Error fetching combined request details:', error);
		throw error;
	}
};

/**
 * Fetch offer details by ID (adapted from specialist version for farmer use)
 */
export const getOfferDetails = async (offerId: string): Promise<OfferDetails> => {
	try {
		const response = await http.get<OfferDetailsResponse>('offers', `get-offer/${offerId}`);

		if (!response.data.success) {
			throw new Error(response.data.error || 'Failed to fetch offer details');
		}

		return response.data.data;
	} catch (error: any) {
		console.error('Error fetching offer details:', error);
		throw error;
	}
};
